import { EditableUserFields } from "client/Api/UserManagement/userManagement";
import {
	Checkbox,
	DatePicker,
	FormItem,
	InputLike,
	InputRow,
	Select,
	SelectItem,
	TextInput,
	ValidationError
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { IFormHandler } from "client/Forms";
import { isNil, hasFlag } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import { FormData } from "./EditUserPage";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import styles from "./SettingsTab.module.less";

export type SettingsTabProps = {
	formHandler: IFormHandler<FormData>;
	editUserFieldsMode: EditableUserFields;
	isEditingUsername: boolean;
	isCurrentUser: boolean;
	domainOptions: readonly SelectItem<string>[] | null;
	timezone: TimeZone;
};

export const SettingsTab = (props: SettingsTabProps): ReactElement => {
	const {
		formHandler,
		editUserFieldsMode,
		isEditingUsername,
		isCurrentUser,
		domainOptions,
		timezone
	} = props;

	const areRestFieldsDisabled: boolean = !hasFlag(editUserFieldsMode, EditableUserFields.TheRest);

	return (
		<>
			<FormItem label={localize("_FirstName")} isRequired>
				<TextInput
					{...formHandler.for("firstName").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("firstName").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_LastName")} isRequired>
				<TextInput
					{...formHandler.for("lastName").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("lastName").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Username")} isRequired>
				<InputRow>
					<InputRow.Stretch>
						<TextInput
							{...formHandler.for("userPrincipalNameName").attachToTextInput()}
							isDisabled={areRestFieldsDisabled || !isEditingUsername}
						/>
					</InputRow.Stretch>

					<InputLike
						value="@"
						isDisabled={areRestFieldsDisabled || !isEditingUsername}
					/>

					<InputRow.Stretch>
						<NoUniqueInputIdContext>
							{isNil(domainOptions) || domainOptions.length === 0
								? (
									<TextInput
										{...formHandler.for("userPrincipalNameDomain").attachToTextInput()}
										isDisabled={areRestFieldsDisabled || !isEditingUsername}
									/>
								)
								: (
									<Select
										{...formHandler.for("userPrincipalNameDomain").attachToSelect()}
										isDisabled={areRestFieldsDisabled || !isEditingUsername}
										options={domainOptions}
										menuTitle={localize("_DomainName")}
									/>
								)
							}
						</NoUniqueInputIdContext>
					</InputRow.Stretch>
				</InputRow>

				<ValidationError {...formHandler.for("userPrincipalName").attachToValidationError()} />
				<ValidationError {...formHandler.for("userPrincipalNameName").attachToValidationError()} />
				<ValidationError {...formHandler.for("userPrincipalNameDomain").attachToValidationError()} />
			</FormItem>


			<FormItem label={localize("_EmailAddress")}>
				<TextInput
					{...formHandler.for("primaryEmail").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("primaryEmail").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_SecondaryEmail")}>
				<TextInput
					{...formHandler.for("secondaryEmail").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("secondaryEmail").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_JobTitle")}>
				<TextInput
					{...formHandler.for("jobTitle").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("jobTitle").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Department")}>
				<TextInput
					{...formHandler.for("department").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("department").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_OfficeLocation")}>
				<TextInput
					{...formHandler.for("officeLocation").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("officeLocation").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_PhoneNumber")}>
				<TextInput
					{...formHandler.for("mobilePhone").attachToTextInput()}
					isDisabled={areRestFieldsDisabled}
				/>

				<ValidationError {...formHandler.for("mobilePhone").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Birthday")}>
				<DatePicker
					{...formHandler.for("birthday").attachToDatePicker()}
					isDisabled={areRestFieldsDisabled}
					mode="date"
					timezone={timezone}
				/>

				<ValidationError {...formHandler.for("birthday").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Active")} isForGroup>
				<Checkbox
					{...formHandler.for("isActive").attachToCheckbox()}
					text={localize("_IsAnActiveUser")}
					isDisabled={areRestFieldsDisabled || isCurrentUser}
					title={isCurrentUser
						? localize("_UnableToUndoYouselfAsAdmin")
						: undefined
					}
				/>

				<ValidationError {...formHandler.for("isActive").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Administrator")} isForGroup>
				<Checkbox
					{...formHandler.for("isAdmin").attachToCheckbox()}
					text={localize("_IsAnAdministrator")}
					isDisabled={isCurrentUser || !hasFlag(editUserFieldsMode, EditableUserFields.IsAdmin)}
					title={isCurrentUser
						? localize("_UnableToUndoYouselfAsAdmin")
						: undefined
					}
				/>

				<ValidationError {...formHandler.for("isAdmin").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_AddressBook")} isForGroup>
				<Checkbox
					{...formHandler.for("isHiddenFromAddressBook").attachToCheckbox()}
					text={localize("_HideUserFromAddressBook")}
					isDisabled={!hasFlag(editUserFieldsMode, EditableUserFields.IsHiddenFromAddressBook)}
				/>
				<ValidationError {...formHandler.for("isHiddenFromAddressBook").attachToValidationError()} />

				<p className={styles.hideFromAddressBookMessage}>
					{localize("_TheUserWontAppearInTheAddressBookOrGlobalSearch")}
				</p>
			</FormItem>
		</>
	);
};
SettingsTab.displayName = "SettingsTab";
